import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { api, setReauthHandler } from "../api/client";
import { Field } from "./Layout";

/* ---------- Toasts ---------- */

interface ToastItem { id: number; text: string; tone?: "error"; action?: { label: string; run: () => void } }
const ToastCtx = createContext<(t: Omit<ToastItem, "id">, ms?: number) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((t: Omit<ToastItem, "id">, ms = 5000) => {
    const id = Date.now() + Math.random();
    setItems((xs) => [...xs, { ...t, id }]);
    window.setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), ms);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast${t.tone === "error" ? " error" : ""}`}>
            <span>{t.text}</span>
            {t.action ? (
              <button onClick={() => { t.action!.run(); setItems((xs) => xs.filter((x) => x.id !== t.id)); }}>{t.action.label}</button>
            ) : null}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ---------- Dialog ---------- */

export function Dialog({ open, onClose, children, labelledBy }: { open: boolean; onClose: () => void; children: ReactNode; labelledBy: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className="dialog" aria-labelledby={labelledBy} onClose={onClose} onCancel={onClose}>
      {open ? children : null}
    </dialog>
  );
}

export function ConfirmDialog({
  open, title, body, confirmLabel, danger, onConfirm, onCancel, busy,
}: { open: boolean; title: string; body: ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => void; onCancel: () => void; busy?: boolean }) {
  return (
    <Dialog open={open} onClose={onCancel} labelledBy="confirm-title">
      <div className="dialog-body">
        <h2 className="dialog-title" id="confirm-title">{title}</h2>
        <div>{body}</div>
        <div className="form-actions">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className={`btn ${danger ? "btn-danger" : "btn-primary"}`} onClick={onConfirm} disabled={busy}>{confirmLabel}</button>
        </div>
      </div>
    </Dialog>
  );
}

/* ---------- Re-confirm password for sensitive actions ---------- */

export function ReauthProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<((ok: boolean) => void) | null>(null);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setReauthHandler(() => new Promise<boolean>((resolve) => {
      setPassword("");
      setError(null);
      setPending(() => resolve);
    }));
    return () => setReauthHandler(null);
  }, []);

  const finish = (ok: boolean) => {
    pending?.(ok);
    setPending(null);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/reauth", { method: "POST", json: { password } });
      finish(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "That password is wrong.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {children}
      <Dialog open={!!pending} onClose={() => finish(false)} labelledBy="reauth-title">
        <form className="dialog-body" onSubmit={submit}>
          <h2 className="dialog-title" id="reauth-title">Confirm it's you</h2>
          <p className="muted">This action changes access or removes data. Enter your password to continue. You won't be asked again for 5 minutes.</p>
          <Field label="Password" htmlFor="reauth-password" error={error}>
            <input id="reauth-password" className="input" type="password" autoComplete="current-password" autoFocus
                   value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={!!error} required />
          </Field>
          <div className="form-actions">
            <button type="button" className="btn" onClick={() => finish(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy || !password}>Continue</button>
          </div>
        </form>
      </Dialog>
    </>
  );
}
